// What a person chose about how the site looks and where it opens, held on the
// account so it follows them (scenarios M2 and L3,
// PLAN-2026-10-02-your-account-follows-you.md).
//
// `MeView.prefs` carries it; `PUT /api/me/prefs` takes a `PrefsUpdate` and
// answers the whole of it again.
//
// WHAT IS NOT HERE: a device's own detail level and text size. "Just on this
// device" is a client's business, kept in that client's storage and never
// sent; the account's values are the default any client may read (§3).
//
// THE LOOK IS NOT AN ENUM. A look is drawn by a client, and two clients need
// not have the same ones — so the account keeps whatever id a client chose,
// and a client that does not know it draws its own default and does not
// write that back (§5).

import { z } from 'zod';
import { DetailLevelSchema, Theme } from '@fsp/theme';

/** A short lowercase id: a look's, or a region's on the League page. */
const ShortId = z
  .string()
  .regex(/^[a-z][a-z0-9-]{0,39}$/, 'Use a short lowercase id: letters, digits and dashes.');

/** A league's id as a key: the shape every league id this API mints has. */
const LeagueKey = z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, 'That is not a league id.');

/** How many leagues may have a screen of their own to open on. */
export const OPENS_ON_MAX = 100;

/**
 * Where a league opens (L3): My Team unless the person chose otherwise. The
 * client maps each to its screen; `scores` is the league's scoreboard and
 * `league` its League page. DESIGN.md §2.15 layer 3 names the first four.
 */
export const OpensOn = z.enum(['team', 'matchup', 'scores', 'players', 'league']);
export type OpensOn = z.infer<typeof OpensOn>;

/**
 * The League page's regions in the person's order, and the ones hidden (L3).
 * One arrangement for every league. A region a client does not know is
 * ignored by it, and a region added later appears in its usual place, so
 * neither list has to name every region there is.
 */
export const LeaguePageLayout = z.object({
  order: z.array(ShortId).max(40),
  hidden: z.array(ShortId).max(40),
});
export type LeaguePageLayout = z.infer<typeof LeaguePageLayout>;

/**
 * The account's achievement choices (ACHIEVEMENTS.md §12): `tellLeagues` is
 * *Tell my leagues when I find one*, on unless the person turns it off. A line
 * posts to a league's chat only when that league's `fun.achievements.postToChat`
 * is on as well.
 */
export const AchievementPrefs = z.object({ tellLeagues: z.boolean() });
export type AchievementPrefs = z.infer<typeof AchievementPrefs>;

/**
 * Chat: whether this person sees the bots' lines in their leagues' chats
 * (B-299). Off, a league's chat leaves them out for this person alone, on
 * every device. Absent is shown.
 */
export const ChatPrefs = z.object({
  hideBots: z.boolean(),
  /**
   * Show messages as written: strong language unmasked, for this person
   * (scenario I9, B-448). Absent or false is masked, which every account is
   * until it chooses. Added 2026-10-19.
   */
  asWritten: z.boolean().optional(),
});
export type ChatPrefs = z.infer<typeof ChatPrefs>;

const OpensOnMap = z
  .record(LeagueKey, OpensOn)
  .refine((m) => Object.keys(m).length <= OPENS_ON_MAX, {
    message: `At most ${OPENS_ON_MAX} leagues at once.`,
  });

export const UserPrefs = z.object({
  /** The look, by id (LOOKS.md). */
  look: ShortId,
  /** Mode, accent, text size and sounds (THEMING.md, `@fsp/theme`). */
  theme: Theme,
  /** How many numbers a screen shows (DETAIL_LEVELS.md). */
  detail: DetailLevelSchema,
  /** The League page's arrangement, or null for the usual order. */
  leaguePage: LeaguePageLayout.nullable(),
  /** Where each league opens; a league absent here opens on My Team. */
  opensOn: OpensOnMap,
  /**
   * Achievements: whether a find posts to the league's chat. Optional because
   * it is additive (a server before A4 does not send it); absent is on.
   */
  achievements: AchievementPrefs.optional(),
  /** Chat: whether the bots' lines show (B-299). Optional because it is additive; absent is shown. */
  chat: ChatPrefs.optional(),
});
export type UserPrefs = z.infer<typeof UserPrefs>;

/**
 * A change to some of it. What is absent is kept.
 *
 * `theme` is replaced whole — a client always holds all four of its fields —
 * and so is `leaguePage`, where null puts the usual order back. `opensOn` is
 * MERGED by league, so two devices choosing for two leagues do not undo each
 * other; `team` for a league puts it back to opening on My Team.
 */
export const PrefsUpdate = z.object({
  look: ShortId.optional(),
  theme: Theme.optional(),
  detail: DetailLevelSchema.optional(),
  leaguePage: LeaguePageLayout.nullable().optional(),
  opensOn: OpensOnMap.optional(),
  achievements: AchievementPrefs.optional(),
  chat: ChatPrefs.optional(),
});
export type PrefsUpdate = z.infer<typeof PrefsUpdate>;
export type PrefsUpdateInput = z.input<typeof PrefsUpdate>;
